import { Component, inject } from '@angular/core';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { RecipientsService } from '../../../../services/campaign/recipients.service';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { ProcessCampaignService } from '../../../../services/campaign/process-campaign.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CancelCampaignService } from '../../../../services/campaign/cancel-campaign.service';
import { AddOrganizationsModalComponent } from '../../../tables/add-organizations-modal/add-organizations-modal.component';
import { IOrganization, ITag } from '../../../../types/contactModule';
import { AddTagsModalComponent } from '../../../tables/add-tags-modal/add-tags-modal.component';
import { AddSegmentsModalComponent } from '../../../tables/add-segments-modal/add-segments-modal.component';
import { IEntitySegmentQuery } from '../../../organization/segments/segment-utilities';
import { WizardAudienceService } from '../../../../pages/organization/campaigns/create-campaign/wizard-audience.service';
import { formatCount } from '../../../../../utils/sms';

@Component({
  selector: 'app-recipients-form',
  standalone: true,
  imports: [
    FormWrapperComponent,
    InputFieldComponent,
    AddOrganizationsModalComponent,
    AddTagsModalComponent,
    AddSegmentsModalComponent,
  ],
  templateUrl: './recipients-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class RecipientsFormComponent {
  recipientsService = inject(RecipientsService);
  campaignStepperService = inject(CampaignStepperService);
  processCampaignService = inject(ProcessCampaignService);
  cancelCampaignService = inject(CancelCampaignService);
  audience = inject(WizardAudienceService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  openOrgModal = false;
  openTagModal = false;
  openSegModal = false;
  selectedOrganizations: IOrganization[] = [];
  selectedTags: ITag[] = [];
  selectedSegments: IEntitySegmentQuery[] = [];

  readonly formatCount = formatCount;

  /** "Choose organizations" or "3 organizations". */
  summary(count: number, one: string, many: string): string {
    if (!count) return `Choose ${many}`;
    return `${formatCount(count)} ${count === 1 ? one : many}`;
  }

  getSelectedOrganizations(organizations: IOrganization[]) {
    this.selectedOrganizations = organizations;
    this.updateService();
    this.validateForm();
  }
  getSelectedTags(tags: ITag[]) {
    this.selectedTags = tags;
    this.updateService();
    this.validateForm();
  }
  getSelectedSegments(segments: IEntitySegmentQuery[]) {
    this.selectedSegments = segments;
    this.updateService();
    this.validateForm();
  }

  updateService() {
    this.recipientsService.recipients.organizations = this.selectedOrganizations;
    this.recipientsService.recipients.tags = this.selectedTags;
    this.recipientsService.recipients.segments = this.selectedSegments;
    // The unique audience follows the selection.
    this.audience.refresh();
  }

  // Remove by the item itself: copied groups can lack an id.
  removeOrganization(target: IOrganization) {
    this.selectedOrganizations = this.selectedOrganizations.filter((item) => item !== target);
    this.updateService();
    this.validateForm();
  }
  removeTag(target: ITag) {
    this.selectedTags = this.selectedTags.filter((item) => item !== target);
    this.updateService();
    this.validateForm();
  }
  removeSegment(target: IEntitySegmentQuery) {
    this.selectedSegments = this.selectedSegments.filter((item) => item !== target);
    this.updateService();
    this.validateForm();
  }

  invalid: boolean = true;
  validateForm() {
    const { organizations, tags, segments } = this.recipientsService.recipients;
    this.invalid = organizations.length < 1 && tags.length < 1 && segments.length < 1;
    // A step that no longer holds up is not done: later steps wait again.
    if (this.invalid) this.markIncomplete('recipients');
  }

  handleSubmit() {
    if (this.invalid) {
      return;
    }
    this.processCampaignService.recipients = this.recipientsService.recipients;
    this.campaignStepperService.stepCompleted('recipients');
    this.campaignStepperService.moveToNextStep('recipients');
  }

  goBack() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: 'campaign-details' },
      queryParamsHandling: 'merge',
    });
  }

  ngOnInit() {
    this.selectedOrganizations = this.recipientsService.recipients.organizations;
    this.selectedTags = this.recipientsService.recipients.tags;
    this.selectedSegments = this.recipientsService.recipients.segments;
    this.invalid =
      !this.selectedOrganizations.length && !this.selectedTags.length && !this.selectedSegments.length;
    this.audience.refresh();
  }

  private markIncomplete(slug: string) {
    const step = this.campaignStepperService.steps.find((item) => item.slug === slug);
    if (step) step.completed = false;
  }
}
